import { type Dispatch, type SetStateAction, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import workspaceServices, {
  type IWorkspaceMember,
} from "#src/services/workspace";
import { apiErrorHandler } from "#src/utils/apiErrorHandler";
import { ButtonV2 } from "#src/commonV2/Button";
import { ModalHeaderIcon } from "#src/commonV2/modal/ModalHeaderIcon";
import { Trash01 } from "@untitled-ui/icons-react";
import { ModalV2 } from "#src/commonV2/modal/ModalV2";

type IDeleteMemberModalProps = {
  open: boolean;
  onClose: () => void;
  selectedMemberIds: number[];
  workspaceId: string;
  memberList: IWorkspaceMember[];
  updateMemberList: () => void;
  setSelectedMemberIds: Dispatch<SetStateAction<number[]>>;
};

export const DeleteMemberModal = ({
  open,
  onClose,
  workspaceId,
  selectedMemberIds,
  memberList,
  updateMemberList,
  setSelectedMemberIds,
}: IDeleteMemberModalProps) => {
  const { t } = useTranslation("workspace", {
    keyPrefix: "member.deleteMemberModal",
  });

  // --------------- DATA ---------------

  const [isLoading, setIsLoading] = useState(false);

  // --------------- COMPUTED ---------------
  const emails = useMemo(() => {
    return memberList
      .filter((m) => selectedMemberIds.includes(m.id))
      .map((m) => m.user?.email || m.email);
  }, [memberList, selectedMemberIds]);

  // --------------- METHODS ---------------

  const deleteMember = async () => {
    if (workspaceId) {
      setIsLoading(true);
      Promise.allSettled(
        selectedMemberIds.map((memberId) => {
          return workspaceServices.remove_member(workspaceId, memberId);
        })
      )
        .then(() => {
          setSelectedMemberIds([]);
          updateMemberList();
          onClose();
        })
        .catch((error) => {
          apiErrorHandler(error, {
            toastMessage: t("notification.delete.fail"),
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  // --------------- RENDER ---------------

  return (
    <ModalV2 open={open} onChangeOpen={onClose} className="gap-8">
      <div className="flex gap-4">
        <ModalHeaderIcon type="error" Icon={Trash01} />
        <div className="flex flex-col gap-5">
          <span className="text-lg font-semibold text-gray-v2-900">
            {t("title")}
          </span>
          {/* Desc */}
          <p className="text-md text-gray-v2-600 whitespace-pre-line">
            <Trans
              t={t}
              i18nKey={"desc"}
              values={{ email: emails.join(", ") }}
              components={{
                span: (
                  <span className="text-md font-semibold text-gray-v2-900" />
                ),
              }}
            />
          </p>
          {/* Desc end */}
        </div>
      </div>
      {/* Footer */}
      <div className="flex gap-3 justify-end">
        <ButtonV2 variant="secondary" size="lg" color="gray" onClick={onClose}>
          <p>{t("button.cancel")}</p>
        </ButtonV2>

        <ButtonV2
          variant="primary"
          size="lg"
          color="error"
          pending={isLoading}
          onClick={deleteMember}
        >
          <p>{t("button.delete")}</p>
        </ButtonV2>
      </div>
      {/* Footer end */}
    </ModalV2>
  );
};
